import * as React from 'react';
import { storyblokEditable, type SbBlokData } from '@storyblok/react';

import { defaultLocale, type LocaleAlternate, type LocaleCode } from '../../config/locales';
import { localizeHref } from '../../storyblok/locales/localizeHref';
import type {
  HeaderStoryblok,
  NavigationItemStoryblok,
  SocialLinkStoryblok,
} from '../../types/storyblok';
import { SiteContainer } from '../layout/SiteContainer';
import { LanguageSelector } from '../ui/LanguageSelector';

// Local logo assets (used when no logo is set in Storyblok)
import gmaLogoDark from '../../assets/logos/gma-logo-dark.svg';
import gmaLogoWhite from '../../assets/logos/gma-logo-white.svg';

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

const useScrolled = (threshold = 50): boolean => {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > threshold);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return isScrolled;
};

const useBodyScrollLock = (isLocked: boolean): void => {
  React.useEffect(() => {
    if (isLocked) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isLocked]);
};

// ─────────────────────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────────────────────

const LinkedInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.5 0H1.5C0.675 0 0 0.675 0 1.5V16.5C0 17.325 0.675 18 1.5 18H16.5C17.325 18 18 17.325 18 16.5V1.5C18 0.675 17.325 0 16.5 0ZM5.37 15.36H2.67V6.75H5.37V15.36ZM4.02 5.565C3.15 5.565 2.445 4.86 2.445 3.99C2.445 3.12 3.15 2.415 4.02 2.415C4.89 2.415 5.595 3.12 5.595 3.99C5.595 4.86 4.89 5.565 4.02 5.565ZM15.36 15.36H12.66V11.16C12.66 10.14 12.645 8.82 11.235 8.82C9.81 8.82 9.585 9.93 9.585 11.085V15.36H6.885V6.75H9.48V7.95H9.51C9.87 7.26 10.77 6.525 12.12 6.525C14.85 6.525 15.36 8.325 15.36 10.68V15.36Z"
      fill="currentColor"
    />
  </svg>
);

const LinkedInIconLarge: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22 0H2C0.9 0 0 0.9 0 2V22C0 23.1 0.9 24 2 24H22C23.1 24 24 23.1 24 22V2C24 0.9 23.1 0 22 0ZM7.16 20.48H3.56V9H7.16V20.48ZM5.36 7.42C4.2 7.42 3.26 6.48 3.26 5.32C3.26 4.16 4.2 3.22 5.36 3.22C6.52 3.22 7.46 4.16 7.46 5.32C7.46 6.48 6.52 7.42 5.36 7.42ZM20.48 20.48H16.88V14.88C16.88 13.52 16.86 11.76 14.98 11.76C13.08 11.76 12.78 13.24 12.78 14.78V20.48H9.18V9H12.64V10.6H12.68C13.16 9.68 14.36 8.7 16.16 8.7C19.8 8.7 20.48 11.1 20.48 14.24V20.48Z"
      fill="currentColor"
    />
  </svg>
);

const HamburgerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 8H28M4 16H28M4 24H28"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 8L24 24M8 24L24 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Mobile Menu Component
// ─────────────────────────────────────────────────────────────────────────────

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navigationItems: NavigationItemStoryblok[];
  socialLinks: SocialLinkStoryblok[];
  logoUrl: string | null;
  logoAlt?: string;
  currentLocale: LocaleCode;
  alternates: readonly LocaleAlternate[];
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  navigationItems,
  socialLinks,
  logoUrl,
  logoAlt,
  currentLocale,
  alternates,
}) => {
  useBodyScrollLock(isOpen);

  // Include Home in mobile navigation
  const mobileNavItems: NavigationItemStoryblok[] = [
    { _uid: 'nav-home', component: 'navigation_item', label: 'Home', url: '/' },
    ...navigationItems,
  ];

  const linkedinLink = socialLinks.find((link) => link.platform === 'linkedin');

  return (
    <div
      className={`
        fixed inset-0 z-50 bg-gradient-to-b from-brand-blue to-brand-deep
        transition-all duration-300 ease-in-out
        ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
      `}
      aria-hidden={!isOpen}
    >
      <div className="flex h-full flex-col px-4 pt-6">
        {/* Header row: Logo + Close button */}
        <div className="flex items-center justify-between">
          <a
            href={localizeHref('/', currentLocale)}
            className="block"
            aria-label="GMA Alliance Home"
            onClick={onClose}
          >
            <img
              src={logoUrl ?? gmaLogoWhite}
              alt={logoAlt ?? 'GMA Alliance'}
              className="h-12 w-auto"
            />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 items-center justify-center text-neutral-white"
            aria-label="Close menu"
          >
            <CloseIcon className="size-8" />
          </button>
        </div>

        {/* Navigation links */}
        <nav className="mt-8 flex flex-1 flex-col items-center">
          <ul className="flex flex-col items-center gap-10">
            {mobileNavItems.map((item) => (
              <li key={item._uid}>
                <a
                  href={localizeHref(item.url, currentLocale)}
                  onClick={onClose}
                  className="text-h6 font-primary font-bold text-neutral-white transition-colors hover:text-secondary-light-sky"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Language selector (hidden by selector itself when alternates < 2).
              Wrapped in text-secondary-ice-blue so the trigger label, globe icon,
              chevron, and bordered input area all pick up the ice-blue color the
              Figma source specifies on the dark mobile drawer. */}
          <div className="mt-16 w-full text-secondary-ice-blue">
            <LanguageSelector
              variant="mobile"
              currentLocale={currentLocale}
              alternates={alternates}
            />
          </div>

          {/* LinkedIn CTA */}
          {linkedinLink && (
            <a
              href={linkedinLink.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="mt-16 flex w-full items-center justify-center gap-2 rounded-lg bg-brand-deep px-8 py-5 shadow-[0_4px_32px_rgba(28,43,58,0.15)] transition-colors hover:bg-neutral-navy"
            >
              <LinkedInIconLarge className="size-8 text-neutral-white" />
              <span className="text-button-large font-primary font-semibold text-neutral-white">
                Message us on LinkedIn
              </span>
            </a>
          )}
        </nav>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Header Component
// ─────────────────────────────────────────────────────────────────────────────

interface HeaderProps {
  blok?: HeaderStoryblok;
  currentLocale?: LocaleCode | null;
  alternates?: readonly LocaleAlternate[];
  /**
   * When true, the logo hides at the top and reveals on scroll (home page behavior).
   * When false, the logo is always visible (inner pages).
   */
  isHomePage?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  blok,
  currentLocale,
  alternates,
  isHomePage = false,
}) => {
  const isScrolled = useScrolled(50);
  // Logo visibility: always show on inner pages, show only when scrolled on home page
  const showLogo = !isHomePage || isScrolled;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Navigation and social links from Storyblok (no fallbacks - must come from CMS)
  const navigationItems = blok?.navigation_items ?? [];
  const socialLinks = blok?.social_links ?? [];
  const logoDarkUrl = blok?.logo?.filename ?? null;
  const logoLightUrl = blok?.logo_light?.filename ?? null;

  // Locale data for the LanguageSelector (R3.1, R5.1). Both props are optional;
  // we fall back to `defaultLocale` so the trigger label always has a valid
  // endonym, and to `[]` so the selector's `alternates.length < 2` guard hides
  // it cleanly on non-Storyblok / single-variant pages (R13.1).
  const effectiveLocale: LocaleCode = currentLocale ?? defaultLocale;
  const effectiveAlternates: readonly LocaleAlternate[] = alternates ?? [];

  const editableProps = blok ? storyblokEditable(blok as unknown as SbBlokData) : {};

  const openMobileMenu = () => setIsMobileMenuOpen(true);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <header
        {...editableProps}
        className="fixed left-0 right-0 top-0 z-40 w-full transition-all duration-300"
      >
        {/*
          Site-wide horizontal extent comes from <SiteContainer>. The `!px-0`
          override forces zero side padding on mobile so the header pill bar
          is full-bleed at mobile (per #50 / Figma mobile-navbar design);
          desktop falls back to the canonical 80 px grid margin via the `!`
          override of the same token. The inner pill bar carries its own
          `px-4` for the rounded background's internal inset, plus
          `desktop:max-w-[1280px]` to cap the pill at exactly the
          SiteContainer's effective inner content width.
        */}
        <SiteContainer className="!px-0 desktop:!px-grid-margin-desktop">
          {/* Inner header bar that gets styled on scroll */}
          <div
            className={`
              mx-auto flex max-w-none items-center justify-between px-4
              transition-all duration-500 ease-out desktop:max-w-[1280px]
              ${
                isScrolled
                  ? 'mt-0 rounded-none bg-neutral-off-white pt-8 pb-2 shadow-[0_4px_64px_rgba(28,43,58,0.15)] desktop:mt-2 desktop:rounded-lg desktop:bg-neutral-white desktop:px-4 desktop:py-3 desktop:shadow-[0_4px_30px_rgba(0,0,0,0.15)]'
                  : 'mt-0 pt-8 pb-2 rounded-none bg-transparent shadow-none backdrop-blur-0 desktop:py-6'
              }
            `}
          >
            {/* Logo - always visible on inner pages; expands from 0 width when scrolled on home page */}
            <a
              href={localizeHref('/', effectiveLocale)}
              className={`block transition-all duration-500 ease-out ${
                showLogo
                  ? 'opacity-100 w-auto max-w-[200px]'
                  : 'opacity-0 w-0 max-w-0 overflow-hidden'
              }`}
              aria-label="GMA Alliance Home"
            >
              <img
                src={isScrolled ? (logoDarkUrl ?? gmaLogoDark) : (logoLightUrl ?? gmaLogoWhite)}
                alt={blok?.logo?.alt ?? 'GMA Alliance'}
                className="h-14 w-auto desktop:h-14"
              />
            </a>

            {/* Desktop Navigation - mx-auto centers when logo hidden, ml-auto pushes right when logo shows */}
            <nav
              className={`hidden items-center gap-16 desktop:flex transition-all duration-500 ease-out ${
                showLogo ? 'ml-auto mr-0' : 'mx-auto'
              }`}
            >
              {/* Menu Items */}
              <ul className="flex items-center gap-8">
                {navigationItems.map((item) => (
                  <li key={item._uid}>
                    <a
                      href={localizeHref(item.url, effectiveLocale)}
                      className={`text-highlight font-medium transition-colors duration-300 ${
                        isScrolled
                          ? 'text-gray-700 hover:text-brand-blue'
                          : 'text-neutral-white hover:text-secondary-light-sky'
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>

              {/* Social Links */}
              <div className="flex items-center gap-4">
                {socialLinks.map((link) => (
                  <a
                    key={link._uid}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex size-6 items-center justify-center rounded-sm p-0.5 transition-colors duration-300 ${
                      isScrolled
                        ? 'text-gray-700 hover:text-brand-blue'
                        : 'text-neutral-white hover:text-secondary-light-sky'
                    }`}
                    aria-label={`Visit our ${link.platform} page`}
                  >
                    {link.platform === 'linkedin' && <LinkedInIcon className="size-[18px]" />}
                  </a>
                ))}
              </div>

              {/* Language selector (hidden by selector itself when alternates < 2).
                  Positioned immediately AFTER the LinkedIn icon group so the
                  visual order matches the Figma desktop header (R3.1). Wrapped
                  in a color context so the trigger inherits gray-700 over the
                  scrolled white header and secondary-ice-blue over the dark
                  hero. */}
              <div
                className={`transition-colors duration-300 ${
                  isScrolled ? 'text-gray-700' : 'text-secondary-ice-blue'
                }`}
              >
                <LanguageSelector
                  variant="desktop"
                  currentLocale={effectiveLocale}
                  alternates={effectiveAlternates}
                />
              </div>
            </nav>

            {/* Mobile Hamburger Button */}
            <button
              type="button"
              onClick={openMobileMenu}
              className={`flex size-8 items-center justify-center desktop:hidden transition-colors duration-300 ${
                isScrolled ? 'text-brand-deep' : 'text-neutral-white'
              }`}
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
            >
              <HamburgerIcon className="size-8" />
            </button>
          </div>
        </SiteContainer>
      </header>

      {/* Mobile Menu Overlay */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
        navigationItems={navigationItems}
        socialLinks={socialLinks}
        logoUrl={logoLightUrl}
        logoAlt={blok?.logo_light?.alt ?? blok?.logo?.alt ?? undefined}
        currentLocale={effectiveLocale}
        alternates={effectiveAlternates}
      />
    </>
  );
};
